import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const articleLinks = [
  { name: 'Complete Spreadsheet Guide', path: '/sugargoo-spreadsheet-guide' },
  { name: 'Shipping Cost Guide', path: '/sugargoo-shipping-cost-guide' },
  { name: 'Is Sugargoo Legit?', path: '/is-sugargoo-legit' },
  { name: 'Coupon & Discount Guide', path: '/sugargoo-coupon-guide' },
  { name: 'Beginner Guide', path: '/sugargoo-beginner-guide' },
  { name: 'Best Finds 2026', path: '/sugargoo-best-finds' },
  { name: 'Warehouse Guide', path: '/sugargoo-warehouse-guide' },
  { name: 'Sugargoo vs Alternatives', path: '/sugargoo-vs-alternatives' },
  { name: 'Shopping Tips', path: '/sugargoo-shopping-tips' },
  { name: 'Spreadsheet Categories', path: '/sugargoo-spreadsheet-categories' },
];

const categoryLinks = [
  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'Hoodies / Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Hoodies.html' },
  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/T-Shirts.html' },
  { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Jackets.html' },
  { name: 'Pants / Shorts', url: 'https://kaspreadsheet.com/search-915/name/Pants.html' },
  { name: 'Headwear', url: 'https://kaspreadsheet.com/search-916/name/Headwear.html' },
  { name: 'Sets', url: 'https://kaspreadsheet.com/search-917/name/Sets.html' },
  { name: 'Underwear / Underpants', url: 'https://kaspreadsheet.com/search-918/name/Underwear.html' },
  { name: 'Jersey', url: 'https://kaspreadsheet.com/search-919/name/Jersey.html' },
  { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Accessories.html' },
  { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Bags.html' },
  { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Belts.html' },
  { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Perfumes.html' },
  { name: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Other.html' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (guideRef.current && !guideRef.current.contains(event.target as Node)) {
        setGuideOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleArticleClick = (path: string) => {
    setMobileOpen(false);
    setGuideOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-2 cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-white">
                <i className="ri-database-2-line text-sm"></i>
              </span>
              <span className="text-sm md:text-base font-heading font-bold text-foreground-950 whitespace-nowrap">
                Sugargoo Hub
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Home
              </Link>

              {/* Shopping Guide Dropdown */}
              <div ref={guideRef} className="relative">
                <button
                  onClick={() => setGuideOpen(!guideOpen)}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Shopping Guides
                  <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </span>
                </button>
                {guideOpen && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-background-200 rounded-xl shadow-lg overflow-hidden z-50">
                    <div className="max-h-80 overflow-y-auto py-1">
                      {articleLinks.map((link) => (
                        <button
                          key={link.path}
                          onClick={() => handleArticleClick(link.path)}
                          className="w-full text-left px-4 py-2.5 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-700 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                        >
                          {link.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="ml-2 px-4 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-xl text-foreground-800 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-background-200">
              <span className="text-sm font-heading font-bold text-foreground-950">Menu</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg text-foreground-700"></i>
              </button>
            </div>

            <div className="p-4">
              {/* Home */}
              <Link
                to="/"
                onClick={() => { setMobileOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors duration-150 cursor-pointer mb-2"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-home-line"></i>
                </span>
                <span className="text-sm font-medium text-foreground-800">Home</span>
              </Link>

              {/* Shopping Guides Accordion */}
              <div className="mb-2">
                <button
                  onClick={() => setCatOpen(!catOpen)}
                  className="w-full flex items-center justify-between px-3 py-3 rounded-xl hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-article-line"></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-800">Shopping Guides</span>
                  </div>
                  <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${catOpen ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-foreground-500 text-xs"></i>
                  </span>
                </button>
                {catOpen && (
                  <div className="ml-11 mt-1 space-y-0.5 border-l-2 border-background-200 pl-3">
                    {articleLinks.map((link) => (
                      <button
                        key={link.path}
                        onClick={() => handleArticleClick(link.path)}
                        className="w-full text-left px-3 py-2 text-sm text-foreground-600 hover:text-accent-700 hover:bg-accent-50/50 rounded-lg transition-colors duration-150 cursor-pointer whitespace-nowrap"
                      >
                        {link.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Category Links */}
              <div className="border-t border-background-200 pt-3 mt-3">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-3 mb-2">Product Categories</p>
                <div className="space-y-0.5">
                  {categoryLinks.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                        <i className="ri-arrow-right-up-line text-xs"></i>
                      </span>
                      <span className="text-sm text-foreground-700">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-4 pt-4 border-t border-background-200">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-shopping-bag-line"></i>
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}